import { WorkflowTemplateService } from './workflowTemplateService';
import { WorkflowTemplate, TemplateMatchingContext } from '../types/journey';
import { JourneyApiService } from './journeyApiService';
import { supabase } from './supabaseClient';

export interface PlatformIntegrationData {
  templateId: string | null;
  stepId: string;
  journeyId: string;
  actionType: 'survey' | 'task' | 'team' | 'preRelease' | 'launchAssets';
  template: WorkflowTemplate | null;
}

export class JourneyPlatformIntegrationService {
  /**
   * Prepares integration data with contextual template matching
   */
  static async prepareIntegrationDataWithContext(
    stepId: string,
    actionType: string,
    journeyId: string,
    context: TemplateMatchingContext,
    templateId?: string
  ): Promise<PlatformIntegrationData> {
    let template: WorkflowTemplate | null = null;

    console.log('🔍 Preparing integration data with context:', {
      stepId,
      actionType,
      journeyId,
      context,
      providedTemplateId: templateId
    });

    // Try to get template by ID first (if provided)
    if (templateId) {
      console.log('📋 Looking up template by ID:', templateId);
      template = await WorkflowTemplateService.getTemplateById(templateId);
      if (template) {
        console.log('✅ Found template by ID:', template.title);
      } else {
        console.log('❌ No template found by ID');
      }
    }

    // If no template found by ID, try contextual matching
    if (!template) {
      console.log('🎯 Trying contextual template matching...');
      template = await WorkflowTemplateService.findMatchingTemplate(context);
      if (template) {
        console.log('✅ Found template by context:', template.title);
      } else {
        console.log('❌ No template found by context');
      }
    }

    const result = {
      templateId: template?.id || null,
      stepId,
      journeyId,
      actionType: actionType as 'survey' | 'task' | 'team' | 'preRelease',
      template
    };

    console.log('📦 Integration data result:', {
      templateId: result.templateId,
      hasTemplate: !!result.template,
      templateTitle: result.template?.title
    });

    return result;
  }

  /**
   * Prepares integration data for platform features (legacy method for backwards compatibility)
   */
  static async prepareIntegrationData(
    stepId: string,
    actionType: string,
    templateId?: string,
    journeyId?: string
  ): Promise<PlatformIntegrationData> {
    let template: WorkflowTemplate | null = null;

    if (templateId) {
      template = await WorkflowTemplateService.getTemplateById(templateId);
    }

    return {
      templateId: template?.id || null,
      stepId,
      journeyId: journeyId || '',
      actionType: actionType as 'survey' | 'task' | 'team' | 'preRelease',
      template
    };
  }

  /**
   * Generates URL for platform feature with template data
   */
  static async generatePlatformUrl(
    integrationData: PlatformIntegrationData,
    selectedIdea?: any
  ): Promise<string> {
    const { actionType, templateId, stepId, journeyId } = integrationData;

    const baseUrls = {
      survey: '/create-questionnaire',
      task: '/create-task',
      team: '/create-team-request',
      preRelease: '/create-pre-release-project',
      launchAssets: '/journey/launch-assets-generator',
      ai: '/journey/ai-idea-generator'
    };

    const baseUrl = baseUrls[actionType];
    if (!baseUrl) {
      throw new Error(`Unknown action type: ${actionType}`);
    }

    const params = new URLSearchParams({
      stepId,
      journeyId,
      source: 'journey-workflow'
    });

    // Only add template parameter if we have a templateId
    if (templateId) {
      params.set('template', templateId);
    }

    // For AI-enhanced pre-release projects, add selected idea information
    console.log('🔍 Checking AI enhancement conditions:', {
      actionType,
      hasSelectedIdea: !!selectedIdea,
      selectedIdea: selectedIdea ? {
        title: selectedIdea.title,
        problem: selectedIdea.problem?.substring(0, 50),
        solution: selectedIdea.solution?.substring(0, 50)
      } : null
    });
    
    if (actionType === 'preRelease' && selectedIdea) {
      console.log('✅ Conditions met! Adding AI-enhanced parameters...');
      params.set('aiEnhanced', 'true');
      params.set('ideaTitle', selectedIdea.title || '');
      params.set('ideaProblem', selectedIdea.problem || '');
      params.set('ideaSolution', selectedIdea.solution || '');
      params.set('ideaTarget', selectedIdea.targetCustomer || selectedIdea.targetAudience || '');
      params.set('ideaFeasibility', selectedIdea.technicalFeasibility || 'medium');
      if (selectedIdea.skillsRequired && Array.isArray(selectedIdea.skillsRequired)) {
        params.set('ideaSkills', selectedIdea.skillsRequired.join(','));
      }
      console.log('✅ Added AI-enhanced pre-release parameters for idea:', selectedIdea.title);
    } else {
      console.log('❌ AI enhancement conditions NOT met:', {
        isPreRelease: actionType === 'preRelease',
        hasIdea: !!selectedIdea
      });
    }

    // For launch assets, try to find the user's latest pre-release project
    if (actionType === 'launchAssets') {
      try {
        // Get current user
        const { data: { user }, error: authError } = await supabase.auth.getUser();
        if (!authError && user) {
          // Import AILaunchAssetsService here to avoid circular dependency
          const { AILaunchAssetsService } = await import('./aiLaunchAssetsService');
          const projectId = await AILaunchAssetsService.getLatestPreReleaseProject(user.id);
          if (projectId) {
            params.set('projectId', projectId);
            console.log('✅ Added projectId to launch assets URL:', projectId);
          } else {
            console.warn('⚠️ No pre-release project found for user, launch assets may not work');
          }
        }
      } catch (error) {
        console.error('❌ Error fetching project for launch assets URL:', error);
      }
    }

    return `${baseUrl}?${params.toString()}`;
  }

  /**
   * Extracts template data for pre-filling forms
   */
  static extractTemplateData(template: WorkflowTemplate, language?: string): any {
    if (!template) {
      console.log('❌ extractTemplateData: No template provided');
      return {};
    }

    console.log('🔍 extractTemplateData: Extracting data for template:', template.title, 'type:', template.type);
    console.log('🌐 extractTemplateData: Language:', language);
    
    let result;
    switch (template.type) {
      case 'survey':
        result = this.extractSurveyData(template, language);
        break;
      case 'task':
        result = this.extractTaskData(template);
        break;
      case 'team':
        result = this.extractTeamData(template);
        break;
      case 'preRelease':
        result = this.extractPreReleaseData(template);
        break;
      default:
        console.log('❌ Unknown template type:', template.type);
        result = {};
    }
    
    console.log('📋 extractTemplateData: Result:', result);
    return result;
  }

  private static extractSurveyData(template: WorkflowTemplate, language?: string): any {
    const content = template.content as any;
    console.log('🔍 extractSurveyData: Raw content:', content);
    console.log('🌐 extractSurveyData: Language:', language);
    
    // Use Chinese translations if language is 'zh' and translations are available
    const useChineseTranslations = language === 'zh';
    
    // Process questions - handle both old string format and new structured format
    let processedQuestions = content.questions || [];
    
    if (Array.isArray(processedQuestions) && processedQuestions.length > 0) {
      // Check if questions are structured objects or simple strings
      const firstQuestion = processedQuestions[0];
      
      if (typeof firstQuestion === 'object' && firstQuestion !== null) {
        // New structured format with options
        processedQuestions = processedQuestions.map((q: any) => {
          if (useChineseTranslations && q.text_zh) {
            return {
              text: q.text_zh,
              type: q.type || 'text',
              options: (q.options_zh && q.options_zh.length > 0) ? q.options_zh : (q.options || [])
            };
          }
          return {
            text: q.text,
            type: q.type || 'text',
            options: q.options || []
          };
        });
      } else if (typeof firstQuestion === 'string') {
        // Old string format - use questions_zh if available
        if (useChineseTranslations && content.questions_zh) {
          processedQuestions = content.questions_zh;
        }
      }
    }
    
    const result = {
      title: (useChineseTranslations && content.title_zh) ? content.title_zh : (content.title || ''),
      description: (useChineseTranslations && content.description_zh) ? content.description_zh : (content.description || ''),
      questions: processedQuestions,
      rewardPoints: content.rewardPoints || 50,
      maxParticipants: content.maxParticipants,
      skillRequired: content.skillRequired || ''
    };
    
    console.log('📋 extractSurveyData: Extracted:', result);
    console.log('📋 extractSurveyData: Using Chinese translations:', useChineseTranslations);
    console.log('📋 extractSurveyData: Processed questions:', processedQuestions.length, 'questions');
    return result;
  }

  private static extractTaskData(template: WorkflowTemplate): any {
    const content = template.content as any;
    return {
      title: content.title || '',
      description: content.description || '',
      instructions: content.instructions || '',
      points: content.points || 100,
      category: content.category || '',
      tags: content.tags || [],
      verificationType: content.verificationType || 'email_username',
      textResponseQuestion: content.textResponseQuestion || '',
      maxParticipants: content.maxParticipants || 1
    };
  }

  private static extractTeamData(template: WorkflowTemplate): any {
    const content = template.content as any;
    return {
      title: content.title || '',
      project_introduction: content.project_introduction || '',
      team_size: content.team_size || 3,
      min_time_commitment: content.min_time_commitment || '',
      skills_required: content.skills_required || [],
      additional_requirements: content.additional_requirements || '',
      deadline: content.deadline || ''
    };
  }

  private static extractPreReleaseData(template: WorkflowTemplate): any {
    const content = template.content as any;
    return {
      title: content.title || '',
      project_type: content.project_type || 'app',
      description: content.description || '',
      concept_details: content.concept_details || '',
      key_features: content.key_features || [{ text: '', screenshot: null }],
      proposed_price: content.proposed_price || '',
      early_bird_discount: content.early_bird_discount || '',
      estimated_timeline: content.estimated_timeline || '',
      project_category: content.project_category || '',
      image_url: content.image_url || '',
      demo_url: content.demo_url || ''
    };
  }

  /**
   * Enriches pre-release project data with journey context information
   */
  static async enrichPreReleaseData(
    journeyId: string,
    templateData: any
  ): Promise<any> {
    try {
      // Fetch journey context
      const { supabase } = await import('./supabaseClient');
      const { data: journey, error } = await supabase
        .from('journeys')
        .select('*')
        .eq('id', journeyId)
        .single();

      if (error || !journey) {
        console.log('Could not fetch journey context, returning template data as-is');
        return templateData;
      }

      const enrichedData = { ...templateData };

      // Enrich concept_details if empty or too short
      if (!enrichedData.concept_details || enrichedData.concept_details.length < 50) {
        enrichedData.concept_details = this.generateConceptDetails(
          journey.market,
          journey.persona,
          journey.stage,
          journey.evaluation,
          enrichedData.description
        );
      }

      // Enrich key_features if empty or insufficient
      if (!enrichedData.key_features || enrichedData.key_features.length === 0 || 
          enrichedData.key_features.every((f: any) => !f.text)) {
        enrichedData.key_features = this.generateKeyFeatures(
          journey.market,
          journey.persona,
          journey.stage
        );
      }

      // Suggest pricing if not provided
      if (!enrichedData.proposed_price) {
        enrichedData.proposed_price = this.suggestPricing(
          journey.market,
          enrichedData.project_type
        );
      }

      // Suggest early bird discount if not provided
      if (!enrichedData.early_bird_discount && enrichedData.proposed_price) {
        enrichedData.early_bird_discount = '15'; // 15% default early bird discount
      }

      // Suggest timeline based on evaluation
      if (!enrichedData.estimated_timeline && journey.evaluation) {
        enrichedData.estimated_timeline = this.suggestTimeline(
          journey.evaluation,
          journey.stage
        );
      }

      // Auto-select project category based on market
      if (!enrichedData.project_category) {
        enrichedData.project_category = this.mapMarketToCategory(journey.market);
      }

      return enrichedData;

    } catch (error) {
      console.error('Error enriching pre-release data:', error);
      return templateData;
    }
  }

  /**
   * Generates detailed concept description based on journey context
   */
  private static generateConceptDetails(
    market: string,
    persona: string,
    stage: string,
    evaluation: any,
    baseDescription: string
  ): string {
    const marketContext = this.getMarketContext(market);
    const personaContext = this.getPersonaContext(persona);
    const stageContext = this.getStageContext(stage);

    let concept = `${baseDescription}\n\n`;

    // Add target audience based on market
    concept += `**Target Audience:** ${marketContext.audience}\n\n`;

    // Add unique value proposition based on persona
    concept += `**Unique Value:** ${personaContext.value}\n\n`;

    // Add development approach based on stage
    concept += `**Development Approach:** ${stageContext.approach}\n\n`;

    // Add goals based on evaluation if available
    if (evaluation) {
      const goals = this.generateGoalsFromEvaluation(evaluation);
      if (goals) {
        concept += `**Key Goals:** ${goals}\n\n`;
      }
    }

    return concept.trim();
  }

  /**
   * Generates suggested key features based on market and persona
   */
  private static generateKeyFeatures(
    market: string,
    persona: string,
    stage: string
  ): Array<{ text: string; screenshot: null }> {
    const features: string[] = [];

    // Market-specific features
    const marketFeatures = this.getMarketFeatures(market);
    features.push(...marketFeatures);

    // Persona-specific features
    const personaFeatures = this.getPersonaFeatures(persona);
    features.push(...personaFeatures);

    // Ensure we have at least 3 features
    while (features.length < 3) {
      features.push('User-friendly interface designed for ease of use');
    }

    return features.slice(0, 5).map(text => ({ text, screenshot: null }));
  }

  /**
   * Suggests pricing based on market and project type
   */
  private static suggestPricing(market: string, projectType: string): string {
    const pricingMap: Record<string, Record<string, string>> = {
      'IT/Tech': {
        'app': '49',
        'website': '99',
        'product': '199',
        'service': '79',
        'other': '59'
      },
      'E-commerce': {
        'app': '39',
        'website': '149',
        'product': '99',
        'service': '69',
        'other': '49'
      },
      'Social Media/Influencer': {
        'app': '29',
        'website': '79',
        'product': '49',
        'service': '99',
        'other': '39'
      },
      'Other': {
        'app': '39',
        'website': '89',
        'product': '79',
        'service': '59',
        'other': '49'
      }
    };

    return pricingMap[market]?.[projectType] || '49';
  }

  /**
   * Suggests timeline based on evaluation and stage
   */
  private static suggestTimeline(evaluation: any, stage: string): string {
    const skillLevel = evaluation.skills || 0;
    const teamSize = evaluation.team || 0;
    const techComfort = evaluation.tech || 0;

    const averageScore = (skillLevel + teamSize + techComfort) / 3;

    if (stage === 'Beginner') {
      return averageScore >= 2 ? '3-6 months' : '6-12 months';
    } else if (stage === 'Have Idea') {
      return averageScore >= 2 ? '2-4 months' : '4-8 months';
    } else { // In Progress
      return averageScore >= 2 ? '1-3 months' : '3-6 months';
    }
  }

  /**
   * Maps market to project category
   */
  private static mapMarketToCategory(market: string): string {
    const categoryMap: Record<string, string> = {
      'IT/Tech': 'technology',
      'E-commerce': 'ecommerce',
      'Social Media/Influencer': 'social',
      'Other': 'other'
    };

    return categoryMap[market] || 'other';
  }

  /**
   * Helper methods for context generation
   */
  private static getMarketContext(market: string): { audience: string } {
    const contexts: Record<string, { audience: string }> = {
      'IT/Tech': {
        audience: 'Tech-savvy users, developers, and early adopters looking for innovative solutions'
      },
      'E-commerce': {
        audience: 'Online shoppers, business owners, and entrepreneurs seeking better commerce tools'
      },
      'Social Media/Influencer': {
        audience: 'Content creators, influencers, and social media users seeking engagement tools'
      },
      'Other': {
        audience: 'General users seeking practical solutions to everyday problems'
      }
    };

    return contexts[market] || contexts['Other'];
  }

  private static getPersonaContext(persona: string): { value: string } {
    const contexts: Record<string, { value: string }> = {
      'skilled-maker': {
        value: 'Leveraging technical expertise to build innovative, cutting-edge solutions'
      },
      'expert-service': {
        value: 'Transforming professional expertise into accessible products and services'
      },
      'founder-plan': {
        value: 'Rapid validation and iteration based on clear market insights'
      }
    };

    return contexts[persona] || contexts['skilled-maker'];
  }

  private static getStageContext(stage: string): { approach: string } {
    const contexts: Record<string, { approach: string }> = {
      'Beginner': {
        approach: 'Starting from scratch with careful planning and learning-focused development'
      },
      'Have Idea': {
        approach: 'Building on a validated concept with focused feature development'
      },
      'In Progress': {
        approach: 'Refining and optimizing an existing product for market readiness'
      }
    };

    return contexts[stage] || contexts['Beginner'];
  }

  private static generateGoalsFromEvaluation(evaluation: any): string {
    const goals: string[] = [];

    if (evaluation.market >= 2) {
      goals.push('Capture identified market opportunity');
    }
    if (evaluation.funding >= 2) {
      goals.push('Optimize available resources for maximum impact');
    }
    if (evaluation.network >= 2) {
      goals.push('Leverage existing network for growth');
    }
    if (evaluation.time >= 2) {
      goals.push('Execute efficiently within available timeframe');
    }

    return goals.join(', ') || 'Build a successful and sustainable product';
  }

  private static getMarketFeatures(market: string): string[] {
    const features: Record<string, string[]> = {
      'IT/Tech': [
        'Advanced API integration for seamless connectivity',
        'Real-time data processing and analytics',
        'Scalable cloud infrastructure'
      ],
      'E-commerce': [
        'Secure payment processing integration',
        'Inventory management system',
        'Customer analytics dashboard'
      ],
      'Social Media/Influencer': [
        'Social media platform integration',
        'Content scheduling and automation',
        'Engagement analytics and insights'
      ],
      'Other': [
        'Intuitive user interface',
        'Cross-platform compatibility',
        'Robust performance optimization'
      ]
    };

    return features[market] || features['Other'];
  }

  private static getPersonaFeatures(persona: string): string[] {
    const features: Record<string, string[]> = {
      'skilled-maker': [
        'AI-powered automation features',
        'Developer-friendly API access'
      ],
      'expert-service': [
        'Professional-grade tools and features',
        'Collaboration and team management'
      ],
      'founder-plan': [
        'Quick deployment and setup',
        'Built-in growth analytics'
      ]
    };

    return features[persona] || [];
  }

  /**
   * Tracks completion of platform actions for journey progress
   */
  static async trackActionCompletion(
    stepId: string,
    actionType: string,
    platformItemId: string
  ): Promise<void> {
    // This would integrate with the journey API to track completion
    // For now, we'll store it in localStorage as a simple implementation
    const completions = this.getStoredCompletions();
    const key = `${stepId}-${actionType}`;
    
    completions[key] = {
      stepId,
      actionType,
      platformItemId,
      completedAt: new Date().toISOString()
    };

    localStorage.setItem('journey-action-completions', JSON.stringify(completions));
  }

  /**
   * Checks if a step action has been completed
   */
  static isActionCompleted(stepId: string, actionType: string): boolean {
    const completions = this.getStoredCompletions();
    const key = `${stepId}-${actionType}`;
    return !!completions[key];
  }

  /**
   * Gets all completed actions for a step
   */
  static getStepCompletions(stepId: string): any[] {
    const completions = this.getStoredCompletions();
    return Object.values(completions).filter((completion: any) => 
      completion.stepId === stepId
    );
  }

  private static getStoredCompletions(): any {
    try {
      const stored = localStorage.getItem('journey-action-completions');
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  }
}